import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import sortByProperty from 'helpers/sortByProperty'

import FormItem from 'js/components/Form/FormItem'
import { Col, Container, Row } from 'js/components/Grid'
import Setting from 'js/components/Setting'
import Spacer from 'js/components/Spacer/Spacer'
import { usePluginsOptions } from 'js/group/AddEditForm/hooks/usePlugins'

import Automembers from './components/Automembers'
import ConfirmFieldModal from './components/ConfirmFieldModal'
import DefaultTags from './components/DefaultTags'
import PluginsOption from './components/PluginsOption'

const SettingsTab = ({
    site,
    viewer,
    autoMemberShipFieldArray,
    plugins,
    setPlugins,
    defaultTags,
    setDefaultTags,
    defaultTagCategories,
    setDefaultTagCategories,
    menu,
    setMenu,
}) => {
    const { t } = useTranslation()

    const { control, watch } = useFormContext()

    const pluginsOptions = usePluginsOptions()

    const profileFieldOptions = site.profileFields.map(({ guid, name }) => ({
        value: guid,
        label: name,
    }))

    // Long text fields cannot be displayed in the members page
    const membersPageProfileFieldOptions = site.profileFields
        .filter(({ fieldType }) => fieldType !== 'htmlField')
        .map(({ guid, name }) => ({
            value: guid,
            label: name,
        }))

    // Exclude long text fields and disable fields that are already displayed on profile card at site level
    const profileCardOptions = site.profileFields
        .filter(({ fieldType }) => fieldType !== 'htmlField')
        .map(({ guid, name, isOnVcard }) => ({
            value: guid,
            label: name,
            disabled: isOnVcard,
            checked: isOnVcard,
            tooltip: isOnVcard
                ? t('entity-group.profile-card-fields-on-vcard')
                : null,
        }))

    return (
        <Container size="small">
            <Row $gutter={40} $spacing={16} style={{ marginTop: '24px' }}>
                <Col mobileLandscapeUp={1 / 2}>
                    <Spacer spacing="small">
                        <Setting
                            subtitle={t('global.types')}
                            helper={t('entity-group.types-helper')}
                            htmlFor="plugins"
                            inputWidth="full"
                        >
                            {pluginsOptions
                                .sort(sortByProperty('label'))
                                .map((option, i) => (
                                    <PluginsOption
                                        key={`plugins-${i}`}
                                        option={option}
                                        plugins={plugins}
                                        setPlugins={setPlugins}
                                        menu={menu}
                                        setMenu={setMenu}
                                    />
                                ))}
                        </Setting>

                        {profileFieldOptions.length > 0 && (
                            <>
                                <FormItem
                                    control={control}
                                    type="checkboxes"
                                    name="showMemberProfileFieldGuids"
                                    title={t('entity-group.members')}
                                    description={t(
                                        'entity-group.members-helper',
                                    )}
                                    options={membersPageProfileFieldOptions}
                                    size="small"
                                />

                                <FormItem
                                    control={control}
                                    type="checkboxes"
                                    name="requiredProfileFieldGuids"
                                    title={t('profile.required-fields')}
                                    description={t(
                                        'profile.required-fields-helper',
                                    )}
                                    options={profileFieldOptions}
                                    size="small"
                                />
                                <FormItem
                                    control={control}
                                    type="textarea"
                                    name="requiredProfileFieldsMessage"
                                    label={t('form.message')}
                                    helper={t(
                                        'profile.required-fields-message-helper',
                                    )}
                                />

                                <FormItem
                                    control={control}
                                    type="checkboxes"
                                    name="vcardFieldGuids"
                                    title={t(
                                        'entity-group.profile-card-fields',
                                    )}
                                    description={t(
                                        'entity-group.profile-card-fields-helper',
                                    )}
                                    options={profileCardOptions}
                                    size="small"
                                />
                            </>
                        )}
                    </Spacer>
                </Col>
                <Col mobileLandscapeUp={1 / 2}>
                    <Spacer spacing="small">
                        {site.chatEnabled && (
                            <FormItem
                                control={control}
                                type="switch"
                                prependField
                                name="isChatEnabled"
                                size="small"
                                title={t('chat.title')}
                                helper={t('entity-group.chat-helper')}
                            />
                        )}
                        <FormItem
                            control={control}
                            type="switch"
                            prependField
                            name="autoNotification"
                            size="small"
                            title={t('entity-group.field-notifications')}
                            helper={t(
                                'entity-group.field-notifications-helper',
                            )}
                        />
                        <FormItem
                            control={control}
                            type="switch"
                            prependField
                            name="fileNotification"
                            size="small"
                            title={t('entity-group.file-notifications')}
                            helper={t('entity-group.file-notifications-helper')}
                        />

                        <FormItem
                            control={control}
                            type="switch"
                            prependField
                            name="isJoinButtonVisible"
                            size="small"
                            title={t('entity-group.allow-join')}
                            helper={t('entity-group.allow-join-helper')}
                        />

                        <FormItem
                            control={control}
                            type="switch"
                            prependField
                            name="isMenuAlwaysVisible"
                            size="small"
                            title={t('entity-group.menu-always-visible')}
                            helper={t(
                                'entity-group.menu-always-visible-helper',
                            )}
                        />

                        <DefaultTags
                            control={control}
                            tagCategories={site.tagCategories}
                            showDefaultTags={watch('showDefaultTags')}
                            defaultTags={defaultTags}
                            setDefaultTags={setDefaultTags}
                            defaultTagCategories={defaultTagCategories}
                            setDefaultTagCategories={setDefaultTagCategories}
                            customTagsAllowed={site.customTagsAllowed}
                        />

                        {viewer?.isAdmin && (
                            <Setting
                                subtitle={t('entity-group.advanced')}
                                inputWidth="full"
                            >
                                <Spacer spacing="small">
                                    <FormItem
                                        control={control}
                                        type="switch"
                                        prependField
                                        name="isFeatured"
                                        size="small"
                                        title={t('entity-group.field-featured')}
                                        helper={t(
                                            'entity-group.field-featured-helper',
                                        )}
                                    />
                                    <FormItem
                                        control={control}
                                        type="switch"
                                        prependField
                                        name="isHidden"
                                        size="small"
                                        title={t('entity-group.hidden')}
                                        helper={t('entity-group.hidden-helper')}
                                    />
                                    <>
                                        <FormItem
                                            control={control}
                                            type="switch"
                                            prependField
                                            name="isAutoMembershipEnabled"
                                            size="small"
                                            title={t(
                                                'entity-group.field-auto-join',
                                            )}
                                            helper={t(
                                                'entity-group.field-auto-join-helper',
                                            )}
                                        />
                                        <ConfirmFieldModal
                                            title={t(
                                                'entity-group.field-auto-join',
                                            )}
                                            fieldName="isAutoMembershipEnabled"
                                        >
                                            {t(
                                                'entity-group.field-auto-join-warning',
                                            )}
                                        </ConfirmFieldModal>
                                    </>
                                    <FormItem
                                        control={control}
                                        type="switch"
                                        prependField
                                        name="isLeavingGroupDisabled"
                                        size="small"
                                        title={t(
                                            'entity-group.field-mandatory',
                                        )}
                                        helper={t(
                                            'entity-group.field-mandatory-helper',
                                        )}
                                    />
                                    {site?.collaboraPerGroup && (
                                        <FormItem
                                            control={control}
                                            type="switch"
                                            prependField
                                            name="isCollaboraEnabled"
                                            size="small"
                                            title={t('entity-group.collabora')}
                                            helper={t(
                                                'entity-group.collabora-helper',
                                            )}
                                        />
                                    )}
                                    <Automembers
                                        autoMemberShipFieldArray={
                                            autoMemberShipFieldArray
                                        }
                                    />
                                </Spacer>
                            </Setting>
                        )}
                    </Spacer>
                </Col>
            </Row>
        </Container>
    )
}

export default SettingsTab
